import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { HiArrowRight, HiMail, HiCalendar } from 'react-icons/hi';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      ref={ref}
      data-testid="contact-section"
      className="section-padding section-dark"
    >
      <div className="container-main">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left - CTA Text */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <p className="overline text-[#7A8B71] mb-6">Get Started</p>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-tight text-[#FDFBF7] mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="contact-headline"
            >
              Ready to Build a More Sustainable Way of Leading?
            </h2>
            <p className="body-large text-[#FDFBF7]/80 mb-10" data-testid="contact-subheadline">
              If you're looking to improve performance, strengthen leadership, and 
              create a healthier work environment, let's connect.
            </p>

            {/* Quick Options */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <a
                href="#calendly"
                className="inline-flex items-center gap-3 px-6 py-4 bg-[#FDFBF7]/10 backdrop-blur border border-[#FDFBF7]/20 rounded-sm text-[#FDFBF7] hover:bg-[#FDFBF7]/20 transition-colors"
                data-testid="contact-calendly-link"
              >
                <HiCalendar className="w-5 h-5" />
                <span>Schedule via Calendly</span>
              </a>
              <a
                href="mailto:hello@susana.com"
                className="inline-flex items-center gap-3 px-6 py-4 bg-[#FDFBF7]/10 backdrop-blur border border-[#FDFBF7]/20 rounded-sm text-[#FDFBF7] hover:bg-[#FDFBF7]/20 transition-colors"
                data-testid="contact-email-link"
              >
                <HiMail className="w-5 h-5" />
                <span>hello@susana.com</span>
              </a>
            </div>
          </motion.div>

          {/* Right - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label htmlFor="name" className="block text-sm text-[#FDFBF7]/70 mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="John Doe"
                  data-testid="contact-input-name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm text-[#FDFBF7]/70 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="john@company.com"
                  data-testid="contact-input-email"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm text-[#FDFBF7]/70 mb-2">
                  Company (Optional)
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Your Company"
                  data-testid="contact-input-company"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm text-[#FDFBF7]/70 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="form-input resize-none"
                  placeholder="Tell me about your challenges and goals..."
                  data-testid="contact-input-message"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#FDFBF7] text-[#0A192F] font-medium rounded-sm hover:bg-[#F3EFEB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                {!isSubmitting && <HiArrowRight className="w-5 h-5" />}
              </button>

              {submitStatus === 'success' && (
                <p className="text-[#9CAF88] text-center" data-testid="contact-success-message">
                  Thank you! Your message has been sent. I'll get back to you soon.
                </p>
              )}

              {submitStatus === 'error' && (
                <p className="text-red-400 text-center" data-testid="contact-error-message">
                  Something went wrong. Please try again or email directly.
                </p>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
